import React from 'react';
import { CellContext } from '@tanstack/react-table';
import cn from 'classnames';

import { Icons, SliderCheckbox } from '@components/common';
import Actions from '@components/common/Actions/Actions';
import AdaptiveTableCell from '@components/common/AdaptiveTableCell';

import { INetworkElement } from '@interfaces/networkElements/networkElements';

import colors from '@constants/colors';
import { columnSizes } from '@constants/table';
import {
	CONNECT_TEXT,
	DELETE_TEXT,
	DISABLE_AUTOSWITCH_TEXT,
	DISABLE_SFTP_TEXT,
	DISCONNECT_TEXT,
	NETWORK_ELEMENTS_TABS_NAMES,
	SUBSCRIBE_SFTP_TEXT,
	SUBSCRIBE_TEXT,
	UNSUBSCRIBE_TEXT,
} from './constants';

import styles from './ManageNetworkElements.module.scss';
import tableStyles from '@components/common/Table/Table.module.scss';

export const getNetworkElementsTabs = () => [
	{
		title: NETWORK_ELEMENTS_TABS_NAMES.networkElements,
		value: NETWORK_ELEMENTS_TABS_NAMES.networkElements,
		isVisible: true,
	},
	{
		title: NETWORK_ELEMENTS_TABS_NAMES.connectionStatusLog,
		value: NETWORK_ELEMENTS_TABS_NAMES.connectionStatusLog,
		isVisible: true,
	},
];

interface IColumns {
	openConfirmModal: (networkElement: INetworkElement | null, text: string) => void;
	openNetworkElementModal: (networkElement?: INetworkElement) => void;
	openAutoSwitchModal: (networkElement?: INetworkElement) => void;
	openSFTPModal: (networkElement?: INetworkElement) => void;
	isViewer?: boolean;
}

export const getNetworkElementsColumns = ({
	openConfirmModal,
	openSFTPModal,
	openAutoSwitchModal,
	openNetworkElementModal,
	isViewer,
}: IColumns) => [
	{
		header: 'Name',
		accessorKey: 'name',
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
	{
		header: 'Type',
		accessorKey: 'type',
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			const { title = '' } = getValue() || {};

			return <AdaptiveTableCell text={title} />;
		},
	},
	{
		header: 'Host',
		accessorKey: 'ipAddress',
		enableSorting: false,
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
	{
		header: 'Port',
		accessorKey: 'netconfPort',
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
	{
		header: 'Middleware',
		accessorKey: 'middlewareName',
		cell: ({ row: { original } }: CellContext<INetworkElement, any>) => {
			const middlewareName = original?.middlewareName;

			return <AdaptiveTableCell text={middlewareName} />;
		},
	},
	{
		header: 'Enable',
		accessorKey: 'enabled',
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<INetworkElement, any>) => {
			const isEnabled = original?.enabled;

			const onChange = () => {
				openConfirmModal(original, isEnabled ? DISCONNECT_TEXT : CONNECT_TEXT);
			};

			return (
				<div className={styles.slider}>
					<SliderCheckbox
						checked={isEnabled}
						disabled={isViewer}
						id={original?.id}
						onChange={onChange}
					/>
				</div>
			);
		},
	},
	{
		header: 'Connection status',
		enableSorting: false,
		accessorKey: 'status',
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			const { color, title } = getValue() || {};

			return (
				<AdaptiveTableCell
					styleInline={{ color }}
					text={title}
				/>
			);
		},
	},
	{
		header: 'Netconf',
		accessorKey: 'netconfStatus',
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			const { color, title } = getValue() || {};

			return (
				<AdaptiveTableCell
					styleInline={{ color }}
					text={title}
				/>
			);
		},
	},
	{
		header: 'Subscription',
		enableSorting: false,
		accessorKey: 'isSubscribed',
		cell: ({ row: { original } }: CellContext<INetworkElement, any>) => {
			const isSubscribed = original?.subscription;
			const isSFTP = original?.directSftp;

			const subscriptionText = isSubscribed
				? UNSUBSCRIBE_TEXT
				: isSFTP
					? SUBSCRIBE_SFTP_TEXT
					: SUBSCRIBE_TEXT;

			const onChange = () => {
				openConfirmModal(original, subscriptionText);
			};

			return (
				<div className={styles.slider}>
					<SliderCheckbox
						checked={isSubscribed}
						disabled={isViewer}
						id={original?.id}
						onChange={onChange}
					/>
				</div>
			);
		},
	},
	{
		header: 'SFTP',
		enableSorting: false,
		accessorKey: 'isSubscribed',
		cell: ({ row: { original } }: CellContext<INetworkElement, any>) => {
			const isSFTP = original?.directSftp;
			const labelClass = cn(styles.switchLabel, {
				[styles.switchLabelInactive]: !isViewer && isSFTP,
				[styles.switchLabelDisabled]: isViewer,
			});

			const onClick = () => {
				if (isViewer) return;

				if (isSFTP) {
					openConfirmModal(original, DISABLE_SFTP_TEXT);
				} else {
					openSFTPModal(original);
				}
			};

			return (
				<div className={styles.switchWrapper}>
					<div className={styles.slider}>
						<SliderCheckbox
							checked={isSFTP}
							disabled={isViewer}
							id={original?.id}
							onChange={onClick}
						/>
					</div>
					<div
						className={styles.switchButtonWrapper}
						onClick={onClick}
					>
						<span className={labelClass}>Server Path</span>
						<span className={styles.switchIcon}>{Icons.viewIcon()}</span>
					</div>
				</div>

			);
		},
	},
	{
		header: 'Auto switch',
		enableSorting: false,
		accessorKey: 'autoSwitch',
		cell: ({ row: { original } }: CellContext<INetworkElement, any>) => {
			const isSFTP = original?.directSftp;
			const isSubscribed = original?.subscription;

			const isEnabled = original?.autoSwitch;

			const isSwitchedBlocked = !isSFTP && !isSubscribed;

			const labelClass = cn(styles.switchLabel, {
				[styles.switchLabelInactive]: !isViewer && !isSFTP && !isSubscribed,
				[styles.switchLabelDisabled]: isViewer && (!isSFTP && !isSubscribed),
			});

			const onClick = () => {
				if (isViewer || isSwitchedBlocked) {
					return;
				}

				if (isEnabled) {
					openConfirmModal(original, DISABLE_AUTOSWITCH_TEXT);
				} else {
					openAutoSwitchModal(original);
				}
			};

			return (
				<div className={styles.switchWrapper}>
					<div className={styles.slider}>
						<SliderCheckbox
							checked={isEnabled}
							disabled={isViewer || isSwitchedBlocked}
							id={original?.id}
							onChange={onClick}
						/>
					</div>
					<div
						className={styles.switchButtonWrapper}
						onClick={onClick}
					>
						<span className={labelClass}>Set time</span>
						<span className={styles.switchIcon}>{Icons.viewIcon()}</span>
					</div>
				</div>

			);
		},
	},
	{
		header: () => <div className={tableStyles.centerColumnHeader}>Actions</div>,
		accessorKey: 'actions',
		enableSorting: false,
		size: columnSizes.size85,
		isHidden: isViewer,
		cell: ({ row: { original } }: CellContext<INetworkElement, any>) => {
			const handleDelete = () => openConfirmModal(original, DELETE_TEXT);
			const handleEdit = () => openNetworkElementModal(original);

			return (
				<Actions
					editHandler={handleEdit}
					gridNumberOfColumns={2}
					handleDelete={handleDelete}
					isDelete
					isEdit
				/>
			);
		},
	},
];

export const getConnectionLogsColumns = () => [
	{
		header: 'Name',
		accessorKey: 'name',
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
	{
		header: 'Event Type',
		accessorKey: 'eventType',
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			const { title = '' } = getValue() || {};

			return <AdaptiveTableCell text={title} />;
		},
	},
	{
		header: 'Vendor',
		accessorKey: 'vendor',
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			const { title = '' } = getValue() || {};

			return <AdaptiveTableCell text={title} />;
		},
	},
	{
		header: 'Reconnection status',
		accessorKey: 'reconnectionStatus',
		enableSorting: false,
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			const { color = colors.indigo70, title = '-' } = getValue() || {};

			return (
				<AdaptiveTableCell
					styleInline={{ color }}
					text={title}
				/>
			);
		},
	},
	{
		header: 'Date & Time',
		accessorKey: 'createdAt',
		cell: ({ getValue }: CellContext<INetworkElement, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
];
